import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { BookOpenIcon, GraduationCapIcon, UserCheckIcon } from "lucide-react";
import { useMemo } from "react";
import {
  useAttendanceRecordsQuery,
  useInstructorCheckInRecordsQuery,
} from "../api/queries";
import { InstructorAttendanceRecordsTable } from "../ui/InstructorAttendanceRecordsTable";
import { StudentAttendanceRecordsTable } from "../ui/StudentAttendanceRecordsTable";

export function AttendanceRecordsPage() {
  const nav = useNavigate({ from: "/app/attendance/records" });
  const search = useSearch({ from: "/app/attendance/records" }) as Record<
    string,
    unknown
  >;
  const activeTab = (search.tab as "students" | "instructors") ?? "students";

  const sortArray = useMemo((): Array<{
    field: string;
    dir: "asc" | "desc";
  }> => {
    const s = search.sort;
    if (Array.isArray(s) && s[0]) {
      const first = s[0] as { field?: string; dir?: string };
      return [
        {
          field: first.field ?? "checkedInAt",
          dir: first.dir === "asc" ? "asc" : "desc",
        },
      ];
    }
    if (typeof s === "string" && s) {
      const field = s.startsWith("-") ? s.slice(1) : s;
      const dir: "asc" | "desc" = s.startsWith("-") ? "desc" : "asc";
      return [{ field, dir }];
    }
    return [{ field: "checkedInAt", dir: "desc" }];
  }, [search.sort]);

  const studentQuery = useMemo(
    () => ({
      limit: Number(search.limit ?? 20),
      offset: Number(search.offset ?? 0),
      sort: sortArray,
    }),
    [search.limit, search.offset, sortArray],
  );

  const instructorQuery = useMemo(
    () => ({
      limit: Number(search.limit ?? 20),
      offset: Number(search.offset ?? 0),
      sort: sortArray,
    }),
    [search.limit, search.offset, sortArray],
  );

  const { data: studentData, isLoading: studentLoading } =
    useAttendanceRecordsQuery(
      activeTab === "students" ? studentQuery : { limit: 1, offset: 0 },
    );
  const { data: instructorData, isLoading: instructorLoading } =
    useInstructorCheckInRecordsQuery(
      activeTab === "instructors" ? instructorQuery : { limit: 1, offset: 0 },
    );

  const studentItems = studentData?.data ?? [];
  const studentTotal = studentData?.meta?.total ?? 0;
  const instructorItems = instructorData?.data ?? [];
  const instructorTotal = instructorData?.meta?.total ?? 0;

  const updateSearch = (updates: Record<string, unknown>) => {
    nav({
      search: { ...search, ...updates },
    });
  };

  const handleTabChange = (value: string) => {
    const tab = value as "students" | "instructors";
    updateSearch({ tab, offset: 0 });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ບັນທຶກການເຂົ້າຮຽນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6">
          <h1 className="font-bold text-2xl">ບັນທຶກການເຂົ້າຮຽນ</h1>
          <p className="mt-1 text-muted-foreground text-sm">
            ດູບັນທຶກການກວດຊື່ຂອງນັກຮຽນ ແລະ ຄູສອນ
          </p>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ບັນທຶກການເຂົ້າຮຽນນັກຮຽນ
                </CardTitle>
                <GraduationCapIcon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">
                {studentLoading ? "..." : studentTotal}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">
                ຈຳນວນບັນທຶກທັງໝົດ
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ບັນທຶກການກວດຊື່ຄູສອນ
                </CardTitle>
                <UserCheckIcon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">
                {instructorLoading ? "..." : instructorTotal}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">
                ຈຳນວນບັນທຶກທັງໝົດ
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ສະແດງໃນຫນ້ານີ້
                </CardTitle>
                <BookOpenIcon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">
                {activeTab === "students"
                  ? studentItems.length
                  : instructorItems.length}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">
                {activeTab === "students" ? "ນັກຮຽນ" : "ຄູສອນ"}
              </p>
            </CardContent>
          </Card>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="mb-4 grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="students" className="gap-2">
              <GraduationCapIcon className="size-4" />
              ນັກຮຽນ
            </TabsTrigger>
            <TabsTrigger value="instructors" className="gap-2">
              <UserCheckIcon className="size-4" />
              ຄູສອນ
            </TabsTrigger>
          </TabsList>
          <div className="rounded-xl border bg-card pt-2">
            {activeTab === "students" && (
              <StudentAttendanceRecordsTable
                data={studentItems}
                isLoading={studentLoading}
                offset={Number(search.offset ?? 0)}
                limit={Number(search.limit ?? 20)}
                totalCount={studentTotal}
                onPaginationChange={(offset, limit) =>
                  updateSearch({ offset, limit })
                }
                sortBy={studentQuery.sort?.[0]?.field ?? "checkedInAt"}
                sortOrder={
                  (studentQuery.sort?.[0]?.dir ?? "desc") as "asc" | "desc"
                }
                onSortingChange={(id, desc) =>
                  updateSearch({
                    sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                    offset: 0,
                  })
                }
              />
            )}

            {activeTab === "instructors" && (
              <InstructorAttendanceRecordsTable
                data={instructorItems}
                isLoading={instructorLoading}
                offset={Number(search.offset ?? 0)}
                limit={Number(search.limit ?? 20)}
                totalCount={instructorTotal}
                onPaginationChange={(offset, limit) =>
                  updateSearch({ offset, limit })
                }
                sortBy={instructorQuery.sort?.[0]?.field ?? "checkedInAt"}
                sortOrder={
                  (instructorQuery.sort?.[0]?.dir ?? "desc") as "asc" | "desc"
                }
                onSortingChange={(id, desc) =>
                  updateSearch({
                    sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                    offset: 0,
                  })
                }
              />
            )}
          </div>
        </Tabs>
      </Main>
    </>
  );
}
